import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import {
  findAddToCartButton,
  findAddToCartPriceContainer,
  updateProductPrice,
  updateAddToCartButtonPrice,
  addPriceShimmer,
  removePriceShimmer,
} from '@theme/utilities';

/**
 * Event fired when a selling plan is selected
 * @extends {Event}
 */
export class SellingPlanUpdateEvent extends Event {
  /**
   * Creates a new SellingPlanUpdateEvent
   * @param {string} sellingPlanId - The ID of the selected selling plan
   * @param {string} sellingPlanGroupId - The ID of the selling plan group
   * @param {Object} data - Additional event data
   * @param {string} [data.variantId] - The variant ID associated with this selection
   * @param {string} [data.price] - The price for this selling plan
   */
  constructor(sellingPlanId, sellingPlanGroupId, data = {}) {
    super(SellingPlanUpdateEvent.eventName, { bubbles: true });
    this.detail = {
      sellingPlanId,
      sellingPlanGroupId,
      data,
    };
  }

  static eventName = 'selling-plan:update';
}

/**
 * A custom element that manages subscription plan selection.
 * Follows the same pattern as variant-picker: manages UI and dispatches events,
 * while dynamically finding/creating form inputs in the product form.
 *
 * @extends Component
 */
class SubscriptionPlansPicker extends Component {
  #abortController = new AbortController();
  /** @type {AbortController | null} */
  #fetchAbortController = null;
  #subscriptionPlansCache = new Map();
  /** @type {string | null} */
  #lastProcessedVariantId = null;
  /** @type {boolean} */
  #isUpdating = false;

  /** @type {HTMLInputElement | null} */
  #sellingPlanInput = null;

  /** @type {HTMLInputElement | null} */
  #sellingPlanGroupInput = null;

  /** @type {HTMLFormElement | null} */
  #productForm = null;

  /** @type {HTMLElement | null} */
  #subscriptionPlansContainer = null;

  /** @type {boolean} */
  #isUpdatingAddToCartPrice = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    // Find and setup form inputs
    this.#setupFormInputs();

    // Listen for variant updates from the variant picker
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    // Listen for radio changes within this component
    this.addEventListener('change', this.#onPlanChange.bind(this));

    // Listen for clicks on subscription radios to handle deselection
    // (clicking an already-checked radio doesn't fire change event)
    this.addEventListener('click', this.#onPlanClick.bind(this), { signal });

    // Listen for details toggle events
    this.querySelectorAll('.select-buy-modes__details').forEach((details) => {
      details.addEventListener('toggle', this.#onDetailsToggle.bind(this), { signal });
    });

    // Find or create subscription plans container
    this.#subscriptionPlansContainer = this.querySelector('.subscription-plans-picker__plans-container');
    if (!this.#subscriptionPlansContainer) {
      // Create container if it doesn't exist
      this.#subscriptionPlansContainer = document.createElement('div');
      this.#subscriptionPlansContainer.className = 'subscription-plans-picker__plans-container';
      
      // Find where to insert it (after header, before any existing plans)
      const header = this.querySelector('.subscription-plans-picker__header');
      if (header) {
        header.insertAdjacentElement('afterend', this.#subscriptionPlansContainer);
      } else {
        this.appendChild(this.#subscriptionPlansContainer);
      }
    }

    // Cache initial subscription plans HTML for current variant
    const currentVariantId = this.#getCurrentVariantId();
    if (currentVariantId) {
      const existingPlans = this.querySelector('.subscription-plans-picker__options');
      if (existingPlans) {
        this.#subscriptionPlansCache.set(String(currentVariantId), existingPlans.outerHTML);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#fetchAbortController?.abort();
  }

  /**
   * Finds the closest product form and ensures selling plan inputs exist.
   * Scoped to the closest section/container to handle multiple products on a page.
   */
  #setupFormInputs() {
    // Find the closest section container (same scope as variant-picker uses)
    const container = this.closest('.buy-tabs-section, .shopify-section, dialog, product-card, .product, .group-block-content');

    if (container) {
      // Look for the product form within the same container
      this.#productForm = container.querySelector('form[data-type="add-to-cart-form"]');

      if (!this.#productForm) {
        // Try finding it through product-form-component within the container
        const productFormComponent = container.querySelector('product-form-component');
        console.log('[SubscriptionPlansPicker] product-form-component in container:', productFormComponent);
        this.#productForm = productFormComponent?.querySelector('form') ?? null;
        console.log('[SubscriptionPlansPicker] Form found via product-form-component:', this.#productForm);
      }
    }

    // Fallback: search globally if not found in container
    if (!this.#productForm) {
      console.log('[SubscriptionPlansPicker] Falling back to global search');
      this.#productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    }

    if (!this.#productForm) {
      const productFormComponent = document.querySelector('product-form-component');
      this.#productForm = productFormComponent?.querySelector('form') ?? null;
    }

    if (!this.#productForm) {
      console.warn('[SubscriptionPlansPicker] Could not find product form!');
      return;
    }

    // Find or create selling_plan input
    this.#sellingPlanInput = this.#productForm.querySelector('input[name="selling_plan"]');
    if (!this.#sellingPlanInput) {
      this.#sellingPlanInput = document.createElement('input');
      this.#sellingPlanInput.type = 'hidden';
      this.#sellingPlanInput.name = 'selling_plan';
      this.#sellingPlanInput.value = '';
      this.#productForm.appendChild(this.#sellingPlanInput);
    } else {
      console.log('[SubscriptionPlansPicker] selling_plan input already exists:', this.#sellingPlanInput);
    }

    // Find or create selling_plan_group input (optional, some setups may not need it)
    this.#sellingPlanGroupInput = this.#productForm.querySelector('input[name="selling_plan_group"]');
    if (!this.#sellingPlanGroupInput) {
      this.#sellingPlanGroupInput = document.createElement('input');
      this.#sellingPlanGroupInput.type = 'hidden';
      this.#sellingPlanGroupInput.name = 'selling_plan_group';
      this.#sellingPlanGroupInput.value = '';
      this.#productForm.appendChild(this.#sellingPlanGroupInput);
    } else {
      console.log('[SubscriptionPlansPicker] selling_plan_group input already exists:', this.#sellingPlanGroupInput);
    }

  }

  /**
   * Handles clicks on subscription radio buttons to detect deselection.
   * @param {Event} event - The click event.
   */
  #onPlanClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    
    // Also check if clicking on the label (which triggers the radio)
    const label = target.closest('label');
    let radio = null;
    
    if (label) {
      radio = label.querySelector('.subscription-plans-picker__radio');
    } else if (target instanceof HTMLInputElement && target.matches('.subscription-plans-picker__radio')) {
      radio = target;
    }
    
    if (!radio || !(radio instanceof HTMLInputElement)) return;

    const radioType = radio.dataset.radioType;

    // Check if clicking an already selected selling_plan radio (deselection)
    // We check BEFORE the click changes the state, so we look at the current checked state
    if (radioType === 'selling_plan' && radio.checked && this.#sellingPlanInput?.value === radio.dataset.sellingPlan) {
      console.log('[SubscriptionPlansPicker] Deselecting subscription plan via click');
      
      // Prevent the default behavior and stop propagation
      event.preventDefault();
      event.stopPropagation();
      
      // Uncheck the radio
      radio.checked = false;
      
      // Clear selling plan inputs
      this.#updateSellingPlan('', '');
      
      // Clear selected label
      this.#updateSelectedLabel('');
      
      // Revert price to original variant price
      this.#revertToOriginalPrice(radio);
      
      // Update the add to cart button price
      const variantId = radio.dataset.variantId || '';
      const variantPrice = this.#getOriginalVariantPrice(variantId);
      if (variantPrice) {
        const addToCartButton = this.#getAddToCartButton();
        if (addToCartButton) {
          const priceContainer = findAddToCartPriceContainer(addToCartButton);
          if (priceContainer) {
            // Update just the price span, preserving structure
            const priceSpan = priceContainer.querySelector('.price');
            if (priceSpan) {
              // Add shimmer before textContent update
              addPriceShimmer(priceContainer);
              priceSpan.textContent = variantPrice;
              // Remove shimmer after update
              setTimeout(() => {
                removePriceShimmer(priceContainer);
              }, 500);
            } else {
              priceContainer.innerHTML = `<span class="price">${variantPrice}</span>`;
              // Add shimmer after innerHTML update
              requestAnimationFrame(() => {
                addPriceShimmer(priceContainer);
                setTimeout(() => {
                  removePriceShimmer(priceContainer);
                }, 500);
              });
            }
          }
        }
      }
      
      // Update button state
      this.#updateAddToCartButtonState();
      
      // Dispatch event with empty selling plan
      this.dispatchEvent(
        new SellingPlanUpdateEvent('', '', {
          variantId: variantId,
          price: variantPrice || '',
        })
      );
    }
  }

  /**
   * Handles radio button changes for subscription options.
   * @param {Event} event - The change event.
   */
  #onPlanChange(event) {
    const target = event.target;

    // The frequency <select> is what the customer operates; the radios stay the
    // component's state. Forward the choice and let the radio's own change event
    // run the rest of this handler.
    if (target instanceof HTMLSelectElement && target.matches('[data-frequency-select]')) {
      this.#selectPlanById(target.value);
      return;
    }

    if (!(target instanceof HTMLInputElement) || !target.matches('.subscription-plans-picker__radio')) return;

    const radioType = target.dataset.radioType;

    if (radioType === 'one_time_purchase') {
      console.log('[SubscriptionPlansPicker] One-time purchase selected, clearing selling plan');
      this.#updateSellingPlan('', '');
      this.#closeAllDetails();
      this.#updateSelectedLabel('');
    } else if (radioType === 'selling_plan') {
      if (!target.dataset.variantId || !target.dataset.sellingPlan) {
        return;
      }

      this.#updateSellingPlan(
        target.dataset.sellingPlan || '',
        target.dataset.sellingPlanGroup || ''
      );
      this.#updateSelectedLabel(target);
      this.#selectSingleUnitInBundles();
      
      setTimeout(() => {
        this.#updatePricesFromFetchedHTML(target);
      }, 150);
    } else {
      // For one-time purchase, update add-to-cart button price from DOM
      this.#updateAddToCartButtonPrice(target);
    }

    // Update button state
    this.#updateAddToCartButtonState();

    // Dispatch event for other components to react
    this.dispatchEvent(
      new SellingPlanUpdateEvent(
        target.dataset.sellingPlan || '',
        target.dataset.sellingPlanGroup || '',
        {
          variantId: target.dataset.variantId,
          price: target.dataset.variantPrice,
        }
      )
    );
  }

  /**
   * Updates the selling plan hidden inputs in the product form.
   * @param {string} planId - The selling plan ID.
   * @param {string} groupId - The selling plan group ID.
   */
  #updateSellingPlan(planId, groupId) {

    // Re-find inputs if they don't exist (in case form was re-rendered)
    if (!this.#sellingPlanInput || !this.#sellingPlanGroupInput) {
      console.log('[SubscriptionPlansPicker] Inputs not found, re-running setup...');
      this.#setupFormInputs();
    }

    if (this.#sellingPlanInput) {
      const oldValue = this.#sellingPlanInput.value;
      this.#sellingPlanInput.value = planId;
    } else {
      console.warn('[SubscriptionPlansPicker] selling_plan input is null!');
    }

    if (this.#sellingPlanGroupInput) {
      const oldValue = this.#sellingPlanGroupInput.value;
      this.#sellingPlanGroupInput.value = groupId;
    } else {
      console.warn('[SubscriptionPlansPicker] selling_plan_group input is null!');
    }

    this.#syncFrequencySelect(planId);

    // Log the form's current state
    if (this.#productForm) {
      const formData = new FormData(this.#productForm);
    }
  }

  /**
   * Handles variant update events from the variant picker.
   * @param {CustomEvent} event - The variant update event.
   */
  #onVariantUpdate = (event) => {
    const variantId = event.detail.resource?.id;
    const variantIdString = variantId ? String(variantId) : null;
    const html = event.detail.data?.html;
    const productId = event.detail.data?.productId;
    const sourceId = event.detail.sourceId;
    const eventTarget = event.target;

    // Only process events from variant-picker component, not from other sources
    const isFromVariantPicker = eventTarget instanceof HTMLElement && 
      (eventTarget.tagName === 'VARIANT-PICKER' || eventTarget.closest('variant-picker') !== null);
    
    // Filter out events from subscription-plans-picker or bundle picker
    if (sourceId === 'product-packs' || !isFromVariantPicker) {
      return;
    }

    // Only process events from variant picker in the same tab (subscription tab)
    const variantPickerElement = eventTarget instanceof HTMLElement && eventTarget.tagName === 'VARIANT-PICKER' 
      ? eventTarget 
      : eventTarget?.closest('variant-picker');
    
    if (variantPickerElement) {
      const variantPickerTab = variantPickerElement.closest('.tab__content') || variantPickerElement.closest('.buy-tabs-section');
      const subscriptionPickerTab = this.closest('.tab__content') || this.closest('.buy-tabs-section');

      if (variantPickerTab && subscriptionPickerTab && variantPickerTab !== subscriptionPickerTab) {
        return;
      }
    }

    // Only process events for our product
    if (productId && this.dataset.productId && productId !== this.dataset.productId) {
      return;
    }

    // Prevent infinite loop: skip if we're currently updating or if this is the same variant we just processed
    if (this.#isUpdating || this.#lastProcessedVariantId === variantIdString) {
      return;
    }

    // Set flag to prevent recursive calls
    this.#isUpdating = true;
    this.#lastProcessedVariantId = variantIdString;

    // Uncheck all subscription radios
    this.#uncheckAllRadios();

    // Clear selling plan inputs
    this.#updateSellingPlan('', '');

    // Clear selected label
    this.#updateSelectedLabel('');

    // Update visible section based on variant (using morph.js)
    // Extract subscription plans from the HTML already fetched by variant picker
    this.#updateVisibleSection(variantId, html)
      .then(() => {
        // Update button state after update completes
        this.#updateAddToCartButtonState();
        
        // Auto-select first subscription if available and component is visible
        requestAnimationFrame(() => {
          setTimeout(() => {
            this.selectFirstSubscription();
            this.#isUpdating = false;
          }, 50);
        });
      })
      .catch((error) => {
        this.#isUpdating = false;
        if (error.name !== 'AbortError') {
          console.error('[SubscriptionPlansPicker] Error updating visible section:', error);
        }
      });
  };

  /**
   * Gets the current variant ID from the product form or URL.
   * @returns {string | null}
   */
  #getCurrentVariantId() {
    if (this.#productForm) {
      const variantInput = this.#productForm.querySelector('input[name="id"]');
      if (variantInput instanceof HTMLInputElement) {
        return variantInput.value;
      }
    }
    
    // Try to get from URL
    const urlParams = new URLSearchParams(window.location.search);
    const variantId = urlParams.get('variant');
    if (variantId) return variantId;

    return null;
  }

  /**
   * Updates subscription plans container using morph.js based on variant ID.
   * @param {string | number | undefined} variantId - The variant ID to show.
   * @param {Document | undefined} html - The HTML document from VariantUpdateEvent (already fetched by variant picker).
   */
  async #updateVisibleSection(variantId, html) {
    if (!variantId) {
      console.warn('[SubscriptionPlansPicker] No variant ID provided');
      return;
    }

    const variantIdString = String(variantId);

    let newPlansElement = null;

    // First, try to extract from the HTML already provided by VariantUpdateEvent
    if (html) {
      // Try multiple selectors to find the subscription plans in the provided HTML
      newPlansElement = html.querySelector(`subscription-plans-picker[data-section-id="${this.dataset.sectionId}"] .subscription-plans-picker__options`);
      if (!newPlansElement) {
        newPlansElement = html.querySelector(`subscription-plans-picker[data-block-id="${this.dataset.blockId}"] .subscription-plans-picker__options`);
      }
      if (!newPlansElement) {
        newPlansElement = html.querySelector('.subscription-plans-picker__options[data-variant-id="' + variantIdString + '"]');
      }
      if (!newPlansElement) {
        // Try finding it within any subscription-plans-picker
        const picker = html.querySelector('subscription-plans-picker');
        if (picker) {
          newPlansElement = picker.querySelector('.subscription-plans-picker__options');
        }
      }
    }

    // If not found in provided HTML, check cache or fetch
    if (!newPlansElement) {
      // Check cache first
      let plansHTML = this.#subscriptionPlansCache.get(variantIdString);

      if (!plansHTML) {
        // Fetch subscription plans HTML for this variant (fallback)
        try {
          plansHTML = await this.#fetchSubscriptionPlansForVariant(variantIdString);
          if (plansHTML) {
            this.#subscriptionPlansCache.set(variantIdString, plansHTML);
          }
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('[SubscriptionPlansPicker] Error fetching subscription plans:', error);
          }
          return;
        }
      }

      if (!plansHTML || !this.#subscriptionPlansContainer) {
        console.warn('[SubscriptionPlansPicker] No plans HTML or container found');
        return;
      }

      // Parse the HTML and extract the subscription plans
      const parser = new DOMParser();
      const doc = parser.parseFromString(plansHTML, 'text/html');
      
      // Try multiple selectors to find the subscription plans
      newPlansElement = doc.querySelector(`subscription-plans-picker[data-section-id="${this.dataset.sectionId}"] .subscription-plans-picker__options`);
      if (!newPlansElement) {
        newPlansElement = doc.querySelector('.subscription-plans-picker__options[data-variant-id="' + variantIdString + '"]');
      }
      if (!newPlansElement) {
        newPlansElement = doc.querySelector('.subscription-plans-picker__options');
      }
      if (!newPlansElement) {
        // Try finding it within subscription-plans-picker
        const picker = doc.querySelector('subscription-plans-picker');
        if (picker) {
          newPlansElement = picker.querySelector('.subscription-plans-picker__options');
        }
      }
    }

    if (!newPlansElement) {
      console.warn('[SubscriptionPlansPicker] Could not find subscription plans in HTML', {
        variantId: variantIdString,
        sectionId: this.dataset.sectionId,
        blockId: this.dataset.blockId,
      });
      return;
    }

    if (!this.#subscriptionPlansContainer) {
      console.warn('[SubscriptionPlansPicker] No subscription plans container found');
      return;
    }

    // Get current plans element (if exists)
    const currentPlansElement = this.#subscriptionPlansContainer.querySelector('.subscription-plans-picker__options');

    if (currentPlansElement) {
      // Morph existing element
      
      morph(currentPlansElement, newPlansElement);
      
      return new Promise((resolve) => {
        requestAnimationFrame(() => {
          this.#attachPlanEventListeners();
          resolve(undefined);
        });
      });
    } else {
      // Insert new element - clone it to ensure it's properly attached to the document
      console.log('[SubscriptionPlansPicker] Inserting new plans element');
      this.#subscriptionPlansContainer.innerHTML = '';
      const clonedElement = newPlansElement.cloneNode(true);
      this.#subscriptionPlansContainer.appendChild(clonedElement);
      
      // Re-attach event listeners
      this.#attachPlanEventListeners();
      
      // Return resolved promise for consistency
      return Promise.resolve();
    }
  }

  /**
   * Fetches subscription plans HTML for a specific variant using Section Rendering API.
   * @param {string} variantId - The variant ID.
   * @returns {Promise<string>} The HTML string containing subscription plans.
   */
  async #fetchSubscriptionPlansForVariant(variantId) {
    const section = this.closest('.shopify-section');
    if (!section) {
      throw new Error('Could not find section element');
    }

    const sectionId = section.id?.replace('shopify-section-', '');
    if (!sectionId) {
      throw new Error('Could not determine section ID');
    }

    // Build URL with variant parameter
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.set('section_id', sectionId);

    console.log('[SubscriptionPlansPicker] Fetching subscription plans:', url.toString());

    const response = await fetch(url.toString(), {
      signal: this.#fetchAbortController?.signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch subscription plans: ${response.statusText}`);
    }

    const html = await response.text();
    
    // Parse and extract just the subscription plans HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const subscriptionWidget = doc.querySelector(`subscription-plans-picker[data-section-id="${this.dataset.sectionId}"]`);
    
    if (!subscriptionWidget) {
      throw new Error('Could not find subscription widget in response');
    }

    const plansElement = subscriptionWidget.querySelector('.subscription-plans-picker__options');
    return plansElement ? plansElement.outerHTML : '';
  }

  /**
   * Attaches event listeners to subscription plan radio buttons.
   */
  #attachPlanEventListeners() {
    // Event listeners are already attached via the component's change event listener
    // But we need to re-attach details toggle listeners
    const { signal } = this.#abortController;
    this.querySelectorAll('.select-buy-modes__details').forEach((details) => {
      details.addEventListener('toggle', this.#onDetailsToggle.bind(this), { signal });
    });
  }

  /**
   * Unchecks all subscription radio buttons.
   */
  #uncheckAllRadios() {
    this.querySelectorAll('.subscription-plans-picker__radio').forEach((radio) => {
      if (radio instanceof HTMLInputElement) {
        radio.checked = false;
      }
    });
  }

  /**
   * Closes all details elements.
   */
  #closeAllDetails() {
    this.querySelectorAll('.select-buy-modes__details').forEach((details) => {
      if (details instanceof HTMLDetailsElement) {
        details.open = false;
      }
    });
  }

  /**
   * Selects the single unit option in product bundles when a subscription is selected.
   */
  #selectSingleUnitInBundles() {
    // Find all product-packs-component elements in the same section
    const section = this.closest('.shopify-section');
    if (!section) return;

    const bundleComponents = section.querySelectorAll('product-packs-component');
    
    bundleComponents.forEach((component) => {
      // Call the public selectSingleUnit method on each bundle component instance
      const bundleElement = /** @type {any} */ (component);
      if (bundleElement && typeof bundleElement.selectSingleUnit === 'function') {
        bundleElement.selectSingleUnit();
      }
    });
  }

  /**
   * Updates both product-price element and add-to-cart button price from the same source.
   * Uses subscription price data from the morphed DOM (after variant update) or constructs from radio data.
   * @param {HTMLInputElement} selectedRadio - The selected subscription radio button.
   */
  async #updatePricesFromFetchedHTML(selectedRadio) {
    const variantId = selectedRadio.dataset.variantId;
    const sellingPlanId = selectedRadio.dataset.sellingPlan;
    
    if (!variantId || !sellingPlanId) return;

    // Get subscription price data from the current DOM (after morph)
    const subscriptionPricesScript = this.querySelector('script[data-subscription]');
    if (!subscriptionPricesScript?.textContent) return;

    let subscriptionPrices;
    try {
      subscriptionPrices = JSON.parse(subscriptionPricesScript.textContent);
    } catch (error) {
      return;
    }
    
    const priceData = Array.isArray(subscriptionPrices)
      ? subscriptionPrices.find((p) => 
          String(p.variant_id) === String(variantId) && 
          String(p.selling_plan_id) === String(sellingPlanId)
        )
      : null;

    if (!priceData) return;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return;

    // Liquid selects the reference using the buy-tabs setting for this variant.
    // Keep the main price badge consistent with the card, frequency and CTA.
    updateProductPrice(sectionElement, {
      ...priceData,
      compare_at_price: priceData.discount_reference_price ?? priceData.original_price,
      compare_at_price_formatted: priceData.discount_reference_price_formatted ?? priceData.original_price_formatted,
    }, this.dataset.productId);
    updateAddToCartButtonPrice(this, priceData);
  }

  /**
   * Reverts the price display to the original variant price (without subscription discount).
   * @param {HTMLInputElement} radio - The radio button that was deselected.
   */
  async #revertToOriginalPrice(radioOrVariantId) {
    const variantId = typeof radioOrVariantId === 'string'
      ? radioOrVariantId
      : radioOrVariantId?.dataset?.variantId;
    if (!variantId) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    const sectionId = section.id?.replace('shopify-section-', '');
    if (!sectionId) return;

    // Abort any pending fetch
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    // Build URL with variant but WITHOUT selling_plan parameter
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    url.searchParams.delete('selling_plan');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url.toString(), {
        signal: this.#fetchAbortController.signal,
      });

      if (!response.ok) {
        console.warn('[SubscriptionPlansPicker] Failed to fetch original price:', response.statusText);
        return;
      }

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      
      // Find product-price element in the response
      const sectionElement = this.closest('.shopify-section');
      if (sectionElement) {
        let productPriceElement = sectionElement.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`);
        if (!productPriceElement) {
          productPriceElement = sectionElement.querySelector('product-price');
        }
        
        if (productPriceElement) {
          // Find the price container in the response HTML
          let responsePriceElement = html.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`);
          if (!responsePriceElement) {
            responsePriceElement = html.querySelector('product-price');
          }
          
          if (responsePriceElement) {
            const responsePriceContainer = responsePriceElement.querySelector('[ref="priceContainer"]');
            const currentPriceContainer = productPriceElement.querySelector('[ref="priceContainer"]');
            
            if (responsePriceContainer && currentPriceContainer instanceof HTMLElement) {
              // Update the price container with the original variant price
              currentPriceContainer.innerHTML = responsePriceContainer.innerHTML;

              // Add shimmer after innerHTML update
              requestAnimationFrame(() => {
                addPriceShimmer(currentPriceContainer);
                setTimeout(() => {
                  removePriceShimmer(currentPriceContainer);
                }, 500);
              });
            }
          }
        }

        // Also revert the add-to-cart button price
        const addToCartButton = this.#getAddToCartButton();
        if (addToCartButton) {
          const btnPriceContainer = findAddToCartPriceContainer(addToCartButton);
          if (btnPriceContainer) {
            // Find the matching price container in the response HTML
            const responseAddToCart = html.querySelector('.add-to-cart-text__content [ref="priceContainer"]');
            if (responseAddToCart) {
              btnPriceContainer.innerHTML = responseAddToCart.innerHTML;
              requestAnimationFrame(() => {
                addPriceShimmer(btnPriceContainer);
                setTimeout(() => { removePriceShimmer(btnPriceContainer); }, 500);
              });
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[SubscriptionPlansPicker] Error reverting to original price:', error);
      }
    }
  }

  /**
   * Gets the original variant price from the page (without subscription discount).
   * @param {string} variantId - The variant ID.
   * @returns {string | null} The formatted price string or null.
   */
  #getOriginalVariantPrice(variantId) {
    if (!variantId) return null;

    // Try to find the variant price from the variant picker or product form
    const section = this.closest('.shopify-section');
    if (!section) return null;

    // Look for variant picker options to get the original price
    const variantPicker = section.querySelector('variant-picker');
    if (variantPicker) {
      // Try to find the selected variant option
      const selectedOption = variantPicker.querySelector(`[data-variant-id="${variantId}"]:checked, [data-variant-id="${variantId}"][selected]`);
      if (selectedOption instanceof HTMLElement) {
        // Check if there's a price data attribute
        const price = selectedOption.dataset.variantPrice || selectedOption.dataset.price;
        if (price) return price;
      }
    }

    // Fallback: try to get from product-price element's current state
    // This might still have subscription price, so we'll rely on the fetch in #revertToOriginalPrice
    return null;
  }

  /**
   * Handles details toggle events for accordion behavior.
   * @param {Event} event - The toggle event.
   */
  #onDetailsToggle(event) {
    const toggledDetails = event.target;
    if (!(toggledDetails instanceof HTMLDetailsElement)) return;

    const isSubscriptionDetails = toggledDetails.querySelector('.subscription-plans-picker__options');
    const allDetails = this.querySelectorAll('.select-buy-modes__details');

    if (isSubscriptionDetails) {
      // Sync all subscription details to the same state
      allDetails.forEach((details) => {
        if (!(details instanceof HTMLDetailsElement)) return;
        const hasSubscriptionOptions = details.querySelector('.subscription-plans-picker__options');
        if (hasSubscriptionOptions && details !== toggledDetails) {
          details.open = toggledDetails.open;
        }
      });

      // Close non-subscription details when subscription is opened
      if (toggledDetails.open) {
        allDetails.forEach((details) => {
          if (!(details instanceof HTMLDetailsElement)) return;
          const hasSubscriptionOptions = details.querySelector('.subscription-plans-picker__options');
          if (!hasSubscriptionOptions && details.open) {
            details.open = false;
          }
        });
      }
    } else {
      // Accordion behavior for non-subscription details
      if (toggledDetails.open) {
        allDetails.forEach((details) => {
          if (details instanceof HTMLDetailsElement && details !== toggledDetails && details.open) {
            details.open = false;
          }
        });
      }
    }

    // Uncheck one-time options when any details is opened
    if (toggledDetails.open) {
      this.querySelectorAll('.subscription-plans-picker__option--one-time input[type="radio"]').forEach((radio) => {
        if (radio instanceof HTMLInputElement) {
          radio.checked = false;
        }
      });
    }

    // Update button state after toggle
    this.#updateAddToCartButtonState();
  }

  /**
   * Gets the closest add-to-cart button relative to this component.
   * Uses the utility function for consistency.
   * @returns {HTMLButtonElement | null}
   */
  #getAddToCartButton() {
    return findAddToCartButton(this);
  }

  /**
   * Updates the add to cart button price display.
   * Uses the same approach as product-packs.js: extracts price from the subscription plans picker's price element.
   * @param {HTMLInputElement} selectedRadio - The selected radio button.
   */
  #updateAddToCartButtonPrice(selectedRadio) {
    // Prevent infinite loops
    if (this.#isUpdatingAddToCartPrice) {
      return;
    }

    this.#isUpdatingAddToCartPrice = true;

    try {
      const addToCartButton = findAddToCartButton(this);
      if (!addToCartButton) {
        console.warn('[SubscriptionPlansPicker] Add-to-cart button not found');
        return;
      }

      const priceContainer = findAddToCartPriceContainer(addToCartButton);
      if (!priceContainer) {
        console.warn('[SubscriptionPlansPicker] Price container not found in add-to-cart button');
        return;
      }

      // Get the price from the subscription widget's price element
      // The subscription plans picker structure: input -> label -> .subscription-plans-picker__label-content -> .subscription-plans-picker__label-right -> .subscription-plans-picker__price
      const subscriptionLabel = selectedRadio.closest('label');
      if (subscriptionLabel) {
        const subscriptionPriceElement = subscriptionLabel.querySelector('.subscription-plans-picker__price');
        if (subscriptionPriceElement && subscriptionPriceElement.textContent?.trim()) {
          const priceText = subscriptionPriceElement.textContent.trim();
          // Find the .price span inside the container and update it
          const priceSpan = priceContainer.querySelector('.price');
          if (priceSpan) {
            // Only update if the price has actually changed
            if (priceSpan.textContent?.trim() !== priceText) {
              // Add shimmer before textContent update (textContent doesn't replace elements)
              addPriceShimmer(priceContainer);
              priceSpan.textContent = priceText;
              // Remove shimmer after update
              setTimeout(() => {
                removePriceShimmer(priceContainer);
              }, 500);
            }
          } else {
            // If no .price span exists, update the container directly
            priceContainer.innerHTML = `<span class="price">${priceText}</span>`;
            // Add shimmer after innerHTML update
            requestAnimationFrame(() => {
              addPriceShimmer(priceContainer);
              setTimeout(() => {
                removePriceShimmer(priceContainer);
              }, 500);
            });
          }
          return;
        }
      }

      // Fallback: use data attribute if price element not found
      const variantPrice = selectedRadio.dataset.variantPrice;
      if (variantPrice) {
        const priceSpan = priceContainer.querySelector('.price');
        if (priceSpan) {
          // Only update if the price has actually changed
          if (priceSpan.textContent?.trim() !== variantPrice.trim()) {
            // Add shimmer before textContent update
            addPriceShimmer(priceContainer);
            priceSpan.textContent = variantPrice;
            // Remove shimmer after update
            setTimeout(() => {
              removePriceShimmer(priceContainer);
            }, 500);
          }
        } else {
          // If no .price span exists, update the container directly
          priceContainer.innerHTML = `<span class="price">${variantPrice}</span>`;
          // Add shimmer after innerHTML update
          requestAnimationFrame(() => {
            addPriceShimmer(priceContainer);
            setTimeout(() => {
              removePriceShimmer(priceContainer);
            }, 500);
          });
        }
      }
    } finally {
      // Reset flag after a microtask to allow any queued events to complete
      Promise.resolve().then(() => {
        this.#isUpdatingAddToCartPrice = false;
      });
    }
  }

  /**
   * Updates the add to cart button disabled state based on selection.
   */
  #updateAddToCartButtonState() {
    const addToCartButton = this.#getAddToCartButton();
    if (!addToCartButton) return;

    const detailsElements = this.querySelectorAll('.select-buy-modes__details');

    // Check if any subscription details are open
    const subscriptionDetailsOpen = Array.from(detailsElements).some((details) => {
      if (!(details instanceof HTMLDetailsElement)) return false;
      const hasSubscriptionOptions = details.querySelector('.subscription-plans-picker__options');
      return hasSubscriptionOptions && details.open;
    });

    if (subscriptionDetailsOpen) {
      // Check if a frequency option is selected in the visible section
      const visibleSection = this.querySelector('.subscription-plans-picker__options');
      const frequencySelected = visibleSection?.querySelector(
        '.subscription-plans-picker__option--frequency input[type="radio"]:checked'
      );

      if (!frequencySelected) {
        addToCartButton.disabled = true;
        addToCartButton.classList.add('disabled');
      } else {
        addToCartButton.disabled = false;
        addToCartButton.classList.remove('disabled');
      }
    } else {
      // Details are closed, enable the button
      addToCartButton.disabled = false;
      addToCartButton.classList.remove('disabled');
    }
  }

  /**
   * Checks if the subscription-plans-picker element is visible on the page.
   * Returns false if hidden in tabs, details elements, or has display:none.
   * @returns {boolean} True if the element is visible, false otherwise.
   */
  #isElementVisible() {
    // Check if element itself is hidden via CSS
    const style = window.getComputedStyle(this);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    // Check if element has no offsetParent (hidden via display:none on parent)
    if (!this.offsetParent && style.position !== 'fixed') {
      return false;
    }

    // Check if element has zero dimensions
    const rect = this.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return false;
    }

    // Check if element is inside a closed <details> element
    let parent = this.parentElement;
    while (parent) {
      if (parent instanceof HTMLDetailsElement && !parent.open) {
        return false;
      }
      parent = parent.parentElement;
    }

    // Check if element is inside a hidden tab (common tab patterns)
    const tabContent = this.closest('.tab__content');
    if (tabContent && !tabContent.classList.contains('is-active')) {
      return false;
    }

    // Check if element is inside a hidden frequency section (radio card layout)
    const frequencySection = this.closest('[data-frequency-section]');
    if (frequencySection && frequencySection.hasAttribute('hidden')) {
      return false;
    }

    // Check for other common hidden tab patterns
    const hiddenTab = this.closest('[role="tabpanel"]:not([aria-hidden="false"])');
    if (hiddenTab && hiddenTab.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    // Check if any parent has display:none
    parent = this.parentElement;
    while (parent && parent !== document.body) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.display === 'none') {
        return false;
      }
      parent = parent.parentElement;
    }

    return true;
  }

  /**
   * Selects the first subscription option automatically.
   * Only selects if the subscription-plans-picker is visible on the page.
   * Public method that can be called from external scripts.
   */
  selectFirstSubscription() {
    if (!this.#isElementVisible()) return;

    const optionsContainer = this.querySelector('.subscription-plans-picker__options');
    if (!optionsContainer) return;

    const firstSubscriptionRadio = optionsContainer.querySelector(
      '.subscription-plans-picker__radio[data-radio-type="selling_plan"]'
    );

    if (!(firstSubscriptionRadio instanceof HTMLInputElement)) return;
    if (!firstSubscriptionRadio.dataset.variantId || !firstSubscriptionRadio.dataset.sellingPlan) return;
    if (firstSubscriptionRadio.checked) return;

    firstSubscriptionRadio.checked = true;
    firstSubscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Private method wrapper for internal use.
   */
  #selectFirstSubscription() {
    this.selectFirstSubscription();
  }

  /**
   * Checks the state radio matching a selling plan id, as if the customer had
   * picked it directly. Used by the frequency <select>.
   * @param {string} planId - The selling plan ID.
   */
  #selectPlanById(planId) {
    if (!planId) return;

    const radio = this.querySelector(
      `.subscription-plans-picker__radio[data-selling-plan="${CSS.escape(planId)}"]`
    );
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Mirrors the current selling plan back onto the frequency <select>, so the
   * control still shows the right option after a variant change or after the
   * subscription mode is re-selected.
   * @param {string} planId - The selling plan ID, empty to leave the select as is.
   */
  #syncFrequencySelect(planId) {
    if (!planId) return;

    const select = this.querySelector('[data-frequency-select]');
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.value === String(planId)) return;

    select.value = String(planId);
  }

  /**
   * Updates the selected label with the subscription name.
   * @param {HTMLInputElement | string} selectedRadio - The selected radio button or empty string to clear.
   */
  #updateSelectedLabel(selectedRadio) {
    const selectedLabel = this.querySelector('.subscription-plans-picker__selected-label');
    if (!selectedLabel) return;

    if (typeof selectedRadio === 'string' || !selectedRadio) {
      // Clear the label
      selectedLabel.textContent = '';
      return;
    }

    // Find the label element associated with the radio button
    const label = selectedRadio.closest('label') || 
                  document.querySelector(`label[for="${selectedRadio.id}"]`);
    
    if (label) {
      // Find the subscription name in the label text
      const labelText = label.querySelector('.subscription-plans-picker__label-text-name');
      if (labelText) {
        // Get text content, excluding any nested spans
        const subscriptionName = labelText.textContent.trim();
        selectedLabel.textContent = subscriptionName;
      }
    }
  }

  /**
   * Gets the currently selected selling plan ID.
   * @returns {string} The selected selling plan ID or empty string.
   */
  get selectedSellingPlanId() {
    return this.#sellingPlanInput?.value || '';
  }

  /**
   * Gets the currently selected selling plan group ID.
   * @returns {string} The selected selling plan group ID or empty string.
   */
  get selectedSellingPlanGroupId() {
    return this.#sellingPlanGroupInput?.value || '';
  }

  /**
   * Public method to deselect all subscription plans.
   * Unchecks all radios, closes details, clears form inputs, and clears selected label.
   */
  deselectAll() {
    // Uncheck all subscription radio buttons
    this.#uncheckAllRadios();

    // Close all details elements
    this.#closeAllDetails();

    // Clear selling plan inputs
    this.#updateSellingPlan('', '');

    // Clear selected label
    this.#updateSelectedLabel('');

    // Update button state
    this.#updateAddToCartButtonState();

    // Revert product price to original variant price (without subscription discount)
    const variantId = this.#getCurrentVariantId();
    if (variantId) {
      this.#revertToOriginalPrice(variantId);
    }
  }
}

if (!customElements.get('subscription-plans-picker')) {
  customElements.define('subscription-plans-picker', SubscriptionPlansPicker);
}

export default SubscriptionPlansPicker;

